import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { API_URL } from "../shared/constants";

const defaultPagination = () => ({
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
});

export function useRevenueTables(year, month = "all") {
  const { getToken } = useAuth();

  const [revenueData, setRevenueData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [methodRows, setMethodRows] = useState([]);
  const [methodPg, setMethodPg] = useState(defaultPagination);
  const [methodLoading, setMethodLoading] = useState(false);

  const [statusRows, setStatusRows] = useState([]);
  const [statusPg, setStatusPg] = useState(defaultPagination);
  const [statusLoading, setStatusLoading] = useState(false);

  const [partnerRows, setPartnerRows] = useState([]);
  const [partnersPg, setPartnersPg] = useState(defaultPagination);
  const [partnersLoading, setPartnersLoading] = useState(false);

  const [modelRows, setModelRows] = useState([]);
  const [modelsPg, setModelsPg] = useState(defaultPagination);
  const [modelsLoading, setModelsLoading] = useState(false);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const periodParams = useCallback(() => {
    const p = new URLSearchParams();
    if (year != null) p.set("year", String(year));
    if (month && month !== "all") p.set("month", String(month));
    return p;
  }, [year, month]);

  const fetchTable = useCallback(
    async (type, page, pageSize) => {
      const p = periodParams();
      p.set("type", type);
      p.set("page", String(page));
      p.set("pageSize", String(pageSize));
      const r = await axios.get(
        `${API_URL}/api/analytics/revenue/table?${p}`,
        authH(),
      );
      return r.data;
    },
    [authH, periodParams],
  );

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const p = periodParams();
      const r = await axios.get(`${API_URL}/api/analytics/revenue?${p}`, authH());
      setRevenueData(r.data);
    } catch {
      setRevenueData(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [authH, periodParams]);

  const loadMethods = useCallback(async () => {
    setMethodLoading(true);
    try {
      const data = await fetchTable(
        "payment-methods",
        methodPg.page,
        methodPg.pageSize,
      );
      setMethodRows(data.rows || []);
      setMethodPg((prev) => ({ ...prev, ...(data.pagination || {}) }));
    } catch {
      setMethodRows([]);
    } finally {
      setMethodLoading(false);
    }
  }, [fetchTable, methodPg.page, methodPg.pageSize]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const data = await fetchTable(
        "payment-status",
        statusPg.page,
        statusPg.pageSize,
      );
      setStatusRows(data.rows || []);
      setStatusPg((prev) => ({ ...prev, ...(data.pagination || {}) }));
    } catch {
      setStatusRows([]);
    } finally {
      setStatusLoading(false);
    }
  }, [fetchTable, statusPg.page, statusPg.pageSize]);

  const loadPartners = useCallback(async () => {
    setPartnersLoading(true);
    try {
      const data = await fetchTable(
        "partners",
        partnersPg.page,
        partnersPg.pageSize,
      );
      setPartnerRows(data.rows || []);
      setPartnersPg((prev) => ({ ...prev, ...(data.pagination || {}) }));
    } catch {
      setPartnerRows([]);
    } finally {
      setPartnersLoading(false);
    }
  }, [fetchTable, partnersPg.page, partnersPg.pageSize]);

  const loadModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const data = await fetchTable("models", modelsPg.page, modelsPg.pageSize);
      setModelRows(data.rows || []);
      setModelsPg((prev) => ({ ...prev, ...(data.pagination || {}) }));
    } catch {
      setModelRows([]);
    } finally {
      setModelsLoading(false);
    }
  }, [fetchTable, modelsPg.page, modelsPg.pageSize]);

  useEffect(() => {
    setMethodPg(defaultPagination());
    setStatusPg(defaultPagination());
    setPartnersPg(defaultPagination());
    setModelsPg(defaultPagination());
  }, [year, month]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const handleMethodPageChange = useCallback(({ pagination: pg }) => {
    setMethodPg((prev) => ({
      ...prev,
      page: pg.currentPage,
      pageSize: pg.itemsPerPage,
    }));
  }, []);

  const handleStatusPageChange = useCallback(({ pagination: pg }) => {
    setStatusPg((prev) => ({
      ...prev,
      page: pg.currentPage,
      pageSize: pg.itemsPerPage,
    }));
  }, []);

  const handlePartnersPageChange = useCallback(({ pagination: pg }) => {
    setPartnersPg((prev) => ({
      ...prev,
      page: pg.currentPage,
      pageSize: pg.itemsPerPage,
    }));
  }, []);

  const handleModelsPageChange = useCallback(({ pagination: pg }) => {
    setModelsPg((prev) => ({
      ...prev,
      page: pg.currentPage,
      pageSize: pg.itemsPerPage,
    }));
  }, []);

  const fetchAllModelsForExport = useCallback(async () => {
    const data = await fetchTable("models", 1, 500);
    return data.rows || [];
  }, [fetchTable]);

  return {
    revenueData,
    summaryLoading,
    methodRows,
    methodPg,
    methodLoading,
    handleMethodPageChange,
    statusRows,
    statusPg,
    statusLoading,
    handleStatusPageChange,
    partnerRows,
    partnersPg,
    partnersLoading,
    handlePartnersPageChange,
    modelRows,
    modelsPg,
    modelsLoading,
    handleModelsPageChange,
    fetchAllModelsForExport,
  };
}
